import { useEffect, useRef, useState } from "react";
import { Bell, ArrowDownToLine, ArrowUpFromLine, User, Wallet, UserPlus } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const POLL_MS = 15000;

const KIND_META = {
  player_deposit: {
    icon: ArrowDownToLine,
    color: "#10b981",
    tone: "rgba(16,185,129,0.18)",
    label: "Deposit",
    verb: "deposited",
  },
  player_withdraw: {
    icon: ArrowUpFromLine,
    color: "#e54b59",
    tone: "rgba(229,75,89,0.18)",
    label: "Withdraw",
    verb: "withdrew",
  },
  commission_credited: {
    icon: Wallet,
    color: "#22c073",
    tone: "rgba(34,192,115,0.22)",
    label: "Commission",
    verb: "credited",
  },
  wallet_adjustment: {
    icon: Wallet,
    color: "#d4af37",
    tone: "rgba(212,175,55,0.22)",
    label: "Wallet",
    verb: "adjusted",
  },
  player_signup: {
    icon: UserPlus,
    color: "#a78bfa",
    tone: "rgba(167,139,250,0.22)",
    label: "Signup",
    verb: "joined with your code",
  },
};

function relTime(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

function fmtAmount(n) {
  return Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const prevUnreadRef = useRef(0);

  const load = async () => {
    try {
      const { data } = await api.get("/aff-panel/notifications", { params: { limit: 30 } });
      const newUnread = Number(data.unread || 0);
      if (newUnread > prevUnreadRef.current && prevUnreadRef.current > 0) {
        const diff = newUnread - prevUnreadRef.current;
        toast(`${diff} new notification${diff === 1 ? "" : "s"}`, {
          description: data.rows?.[0]?.title || "",
          duration: 3000,
        });
      }
      prevUnreadRef.current = newUnread;
      setRows(data.rows || []);
      setUnread(newUnread);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAllRead = async () => {
    if (unread === 0) return;
    setBusy(true);
    try {
      await api.post("/aff-panel/notifications/mark-read", {});
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg p-btn-ghost"
        aria-label="Notifications"
        data-testid="promo-notifications-bell"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full text-[10px] font-black flex items-center justify-center px-1"
            style={{
              background: "linear-gradient(180deg, #f06270, #c4304a)",
              color: "#fff",
              border: "1.5px solid #0a0d18",
              boxShadow: "0 2px 8px rgba(229,75,89,0.6)",
              animation: "p-pulse-gold 2.2s ease-out infinite",
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Mobile backdrop (closes panel on tap) */}
          <div
            className="sm:hidden fixed inset-0 z-30"
            style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
            onClick={() => setOpen(false)}
          />
          <div
            className="p-fade-up
              fixed left-2 right-2 top-[68px] z-40
              sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2
              sm:w-[380px] rounded-xl overflow-hidden"
            style={{
              background: "linear-gradient(180deg, rgba(22,18,30,0.97), rgba(8,6,14,0.99))",
              border: "1px solid rgba(34, 192, 115, 0.4)",
              boxShadow: "0 30px 80px rgba(0,0,0,0.65), 0 0 0 1px rgba(34, 192, 115, 0.15) inset",
              maxHeight: "calc(100vh - 96px)",
            }}
          >
          <div className="flex items-center justify-between px-4 py-3" style={{
            borderBottom: "1px solid rgba(34, 192, 115, 0.2)",
            background: "linear-gradient(90deg, rgba(34, 192, 115, 0.08), transparent)",
          }}>
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-emerald-300" />
              <div className="text-[13px] font-bold p-platinum-text">Player Activity</div>
              {unread > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{
                  background: "rgba(229,75,89,0.18)", border: "1px solid rgba(229,75,89,0.45)", color: "#f87171",
                }}>{unread}</span>
              )}
            </div>
            <button onClick={markAllRead} disabled={busy || unread === 0} className="text-[11px] font-bold uppercase tracking-widest text-emerald-300 hover:text-emerald-200 disabled:opacity-40">
              Mark all read
            </button>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 160px)" }}>
            {rows.length === 0 ? (
              <div className="py-10 text-center text-white/55 text-[13px]">
                <Bell size={26} className="mx-auto mb-2 opacity-50" />
                No deposit / withdraw activity yet
              </div>
            ) : (
              rows.map((n) => {
                const meta = KIND_META[n.kind] || { icon: Bell, color: "#94a3b8", tone: "rgba(148,163,184,0.16)", label: "", verb: "" };
                const Icon = meta.icon;
                const username = n.player_username || (n.body || "").split(" ")[0] || "Player";
                const amount = Number(n.amount || 0);
                const method = n.method || "";
                return (
                  <div
                    key={n.id || n.created_at + (n.title || "")}
                    className="flex items-start gap-3 px-4 py-3 transition"
                    style={{
                      borderBottom: "1px solid rgba(255,255,255,0.04)",
                      background: n.read ? "transparent" : "linear-gradient(90deg, rgba(34, 192, 115, 0.04), transparent)",
                    }}
                  >
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{
                      background: meta.tone,
                      border: `1px solid ${meta.color}66`,
                      color: meta.color,
                    }}>
                      <Icon size={15} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-[12px] font-black uppercase tracking-wider" style={{ color: meta.color }}>
                          Successful {meta.label}
                        </div>
                        {!n.read && <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: "#22c073", boxShadow: "0 0 8px rgba(34, 192, 115, 0.6)" }} />}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <User size={11} className="text-emerald-300/70" />
                        <span className="text-[13px] font-bold text-white">{username}</span>
                        <span className="text-[11.5px] text-white/55">{meta.verb}</span>
                        {amount > 0 && (
                          <span className="font-mono text-[13px] font-black p-gold-text">৳ {fmtAmount(amount)}</span>
                        )}
                      </div>
                      {method && (
                        <div className="text-[11px] text-white/55 mt-0.5">via <span className="text-emerald-200 font-semibold">{method}</span></div>
                      )}
                      <div className="text-[10px] text-white/35 mt-1 uppercase tracking-widest">{relTime(n.created_at)}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
}
